import moment from "moment";
import { BiSolidTrash } from "react-icons/bi";
import { FaEye } from "react-icons/fa6";
import { MdRefresh } from "react-icons/md";
import { FiPackage, FiExternalLink, FiCopy } from "react-icons/fi";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";
import { Tooltip } from "antd";
import { getItemQuantity } from "../../utility";

// ─── USPS tracking number prefix → service name ──────────────────────────────
// https://postalpro.usps.com/mailing/parcels/TrackingServiceID
const resolveUspsService = (trackNum = "") => {
  const n = trackNum.replace(/\s/g, "");
  if (n.startsWith("94")) return "Priority Mail";
  if (n.startsWith("92")) return "Priority Mail";
  if (n.startsWith("93")) return "First-Class Mail";
  if (n.startsWith("9400")) return "Priority Mail";
  if (n.startsWith("9205")) return "Priority Mail Express";
  if (n.startsWith("82") || n.startsWith("83")) return "USPS Retail Ground";
  return "USPS Package";
};

// ─── Component ───────────────────────────────────────────────────────────────
export const TrackingUspsItem = ({
  trackingData,
  labelImage,
  deleteTracking,
  trackingNumber,
  id,
  trackingType,
  line_items,
  itemIndex,
  orderLineItems,
  product,
  date,
  shippingCost,
  refreshTrackingData,
  loading,
}) => {

  // ════════════════════════════════════════════════════════════════════
  // DATA EXTRACTION
  //
  // USPS structure is flat:
  //   labelData.trackingData = "9361289737048176294363"  (plain string)
  //   labelData.trackingNumber = same
  //   labelData.type = "usps"
  //   labelData.trackingType = "Direct Ship" | "Coming To Techmicro"
  //
  // There is NO rich tracking API data stored — just the tracking number.
  // ════════════════════════════════════════════════════════════════════

  const trackNum = trackingNumber || (typeof trackingData === "string" ? trackingData : "") || "";
  const serviceName = resolveUspsService(trackNum);
  const weight = orderLineItems?.[itemIndex]?.weight;

  const isDirectShip = trackingType === "Direct Ship" || !trackingType;
  const isComingToUS = trackingType === "Coming To Techmicro";

  const accentColor = isDirectShip ? "#22c55e" : isComingToUS ? "#3b82f6" : "#64748b";
  const containerBg = isDirectShip ? "#f0fdf4" : isComingToUS ? "#eff6ff" : "#f8fafc";
  const containerBorder = isDirectShip ? "1px solid #86efac" : isComingToUS ? "1px solid #bfdbfe" : "1px solid #e2e8f0";
  const containerShadow = isDirectShip ? "0 4px 6px rgba(34,197,94,0.15)" : isComingToUS ? "0 4px 6px rgba(59,130,246,0.15)" : "0 4px 6px rgba(100,116,139,0.15)";
  const typeBg = isDirectShip ? "#f0fdf4" : isComingToUS ? "#eff6ff" : "#f8fafc";
  const typeColor = isDirectShip ? "#166534" : isComingToUS ? "#1d4ed8" : "#475569";
  const typeBorder = isDirectShip ? "#bbf7d0" : isComingToUS ? "#bfdbfe" : "#e2e8f0";

  const qty = getItemQuantity(line_items, product);

  const uspsTrackUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackNum}`;

  const cell = (flexBasis, extra = {}) => ({
    flex: `0 1 ${flexBasis}`,
    minWidth: 0,
    padding: "0 8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
    ...extra,
  });

  const label = (txt) => (
    <span style={{ fontSize: "10px", fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
      {txt}
    </span>
  );

  return (
    <div style={{
      display: "flex",
      alignItems: "stretch",
      width: "100%",
      marginBottom: "6px",
      borderRadius: "8px",
      overflow: "hidden",
      border: containerBorder,
      background: containerBg,
      boxShadow: containerShadow,
      minHeight: "60px",
      boxSizing: "border-box",
    }}>

      {/* Accent bar */}
      <div style={{ width: "4px", background: accentColor, flexShrink: 0 }} />

      {/* ── CARRIER + TRACKING # ── */}
      <div style={cell("22%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px" }}>
          <span style={{
            background: "#333366", color: "white",
            fontSize: "9px", fontWeight: 800,
            padding: "1px 6px", borderRadius: "3px", letterSpacing: "0.5px",
          }}>USPS</span>
          <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 500 }}>
            {serviceName}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <a
            href={uspsTrackUrl}
            target="_blank" rel="noreferrer"
            style={{ fontSize: "12px", fontWeight: 700, color: "#0369a1", textDecoration: "none", fontFamily: "monospace", letterSpacing: "0.3px", lineHeight: 1.3, display: "flex", alignItems: "center", gap: "4px" }}
          >
            {trackNum}
            <FiExternalLink size={10} color="#94a3b8" />
          </a>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigator.clipboard.writeText(trackNum);
              toast.success("Copied to clipboard");
            }}
            style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex", alignItems: "center" }}
            title="Copy Tracking Number"
          >
            <FiCopy size={13} color="#64748b" />
          </button>
        </div>
      </div>

      {/* ── STATUS — no live tracking stored ── */}
      <div style={cell("25%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700,
          backgroundColor: "#f8fafc", color: "#475569",
          border: "1px solid #86efac",
          width: "fit-content",
        }}>
          <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#94a3b8", display: "inline-block" }} />
          Tracking Stored
        </span>
        <span style={{ fontSize: "11px", color: "#94a3b8", marginTop: "3px", display: "flex", alignItems: "center", gap: "3px" }}>
          <FiExternalLink size={9} />
          Click refresh or track on USPS
        </span>
      </div>

      {/* ── SERVICE NAME ── */}
      <div style={cell("20%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        {label("Service")}
        <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
          {serviceName}
        </span>
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>United States Postal Service</span>
      </div>

      {/* ── DATE ── */}
      <div style={cell("13%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Created")}
        <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
          {date ? moment(date).format("MMM DD, YYYY") : "—"}
        </span>
      </div>

      {/* ── PACKAGE ── */}
      <div style={cell("13%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Package")}
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {weight && (
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
              ⚖ {weight} <span style={{ fontSize: "11px", fontWeight: 400, color: "#94a3b8" }}>LBS</span>
            </span>
          )}
          <span style={{ fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "3px" }}>
            <FiPackage size={11} color="#94a3b8" />
            Qty: <strong style={{ color: "#334155" }}>{qty}</strong>
          </span>
          {shippingCost && shippingCost !== "N/A" && (
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>Cost: <strong style={{ color: "#475569" }}>{shippingCost}</strong></span>
          )}
        </div>
      </div>

      {/* ── TYPE ── */}
      <div style={cell("10%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Type")}
        {trackingType && (
          <span style={{
            padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700,
            backgroundColor: typeBg,
            color: typeColor,
            border: `1px solid ${typeBorder}`,
            width: "fit-content",
          }}>{trackingType}</span>
        )}
      </div>

      {/* ── ACTIONS — fixed 104px ── */}
      <div style={{
        flexShrink: 0, flexGrow: 0, width: "104px",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "12px", padding: "8px 10px", backgroundColor: "#fafafa",
      }}>
        {labelImage && (
          <button title="View Label" onClick={() => window.open(labelImage, "_blank")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
            <FaEye color="#0369a1" size={19} />
          </button>
        )}
        {loading ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <Tooltip title="Reload to see the latest status of tracking">
            <button onClick={() => refreshTrackingData(id)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
              <MdRefresh color="#0ea5e9" size={21} />
            </button>
          </Tooltip>
        )}
        <button title="Delete" onClick={() => deleteTracking(id)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
          <BiSolidTrash color="#ef4444" size={19} />
        </button>
      </div>
    </div>
  );
};
